import React, { useContext, useState, useEffect } from "react";
import { PageTemplate, HeaderTemplate, CheckoutStyles, ButtonLink } from "../styles";
import { DataContext } from "../DataContext";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "../components/CheckoutForm";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { serverURL } from "../utils";

const stripePromise = loadStripe(
  "pk_test_51NUZ2mCCjwAhiN6N1aC41axsB23mW6ZmuwvxXxhfjcutXIy9ts5CHLP1aWWh7vSq0vqHl7SNp2vmNAVkpvIWGCpV00KsCojHcH",
);

const Checkout = () => {
  const [clientSecret, setClientSecret] = useState("");
  const [checkoutToken, setCheckoutToken] = useState("");
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { productsInCart } = useContext(DataContext);
  const navigate = useNavigate();

  let headers = {
    "Content-Type": "application/json",
  };

  const checkoutElements = async () => {
    if (isAuthenticated && !isLoading) {
      const token = await getAccessTokenSilently();
      headers = { ...headers, authorization: `Bearer ${token}` };
    }
    const fetchRoute = isAuthenticated && !isLoading ? "create-pi" : "create-pi-guest";
    const response = await fetch(`${serverURL()}/orders/${fetchRoute}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ cart: productsInCart }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const data = await response.json();

    // Keep authorization only in this browser tab for Stripe redirect completion.
    const paymentIntentId = data.clientSecret?.split("_secret_")[0];
    if (!paymentIntentId || !data.checkoutToken) throw new Error("Missing checkout authorization.");
    sessionStorage.setItem(`gg-checkout-${paymentIntentId}`, data.checkoutToken);
    setCheckoutToken(data.checkoutToken);
    setClientSecret(data.clientSecret);
  };

  useEffect(() => {
    if (productsInCart.length > 0) {
      const fetchData = async () => {
        try {
          await checkoutElements();
        } catch (error) {
          console.error(error.message);
          toast.error(error.message);
        }
      };
      fetchData();
    } else {
      navigate("/bag");
    }
  }, []);

  const appearance = {
    theme: "stripe",

    variables: {
      fontFamily: "Figtree, sans-serif",
      colorPrimary: "#53555a",
      colorText: "#53555a",
      fontSmooth: "antialiased",
      fontSizeSm: "16px",
      fontWeightNormal: "300",
      spacingUnit: "5px",
      colorDanger: "#da471f",
    },
    rules: {
      ".Input": {
        fontWeight: "700",
        boxShadow: "none",
        backgroundColor: "#f9f9f9",
      },
      ".Input:focus": {
        border: "1px solid #f68b1f",
        outline: "1px solid #f68b1f",
        boxShadow: "none",
      },
      ".Input--invalid:focus": {
        border: "1px solid #da471f",
        outline: "2px solid #f68b1f",
        outlineOffset: "2px",
        boxShadow: "none",
      },
      ".Tab": {
        boxShadow: "none",
        border: "1px solid #e7e7e7",
        backgroundColor: "#fafafa",
      },
      ".Tab--selected": {
        boxShadow: "none",
        border: "1px solid #97a3ae",
        backgroundColor: "#eeeeee",
      },
      ".Tab:focus": {
        boxShadow: "none",
        border: "1px solid #eeeeee",
        outline: "0",
      },
      ".Tab--selected:focus": {
        boxShadow: "none",
        border: "1px solid #97a3ae",
        outline: "0",
      },
      ".Tab:focus-visible": {
        boxShadow: "none",
        border: "1px solid #f68b1f",
        outline: "1px solid #f68b1f",
      },
      ".Tab--selected:focus-visible": {
        boxShadow: "none",
        border: "1px solid #f68b1f",
        outline: "1px solid #f68b1f",
      },
    },
  };

  const options = {
    clientSecret,
    appearance,
    fonts: [
      {
        cssSrc: "https://fonts.googleapis.com/css2?family=Figtree&display=swap",
      },
    ],
  };

  return (
    <CheckoutStyles>
      <HeaderTemplate className="narrow">
        <div className="header">
          <div className="title">
            <h1>Checkout</h1>
          </div>
        </div>
      </HeaderTemplate>
      <PageTemplate className="narrow">
        <div className="App">
          {clientSecret && (
            <Elements options={options} stripe={stripePromise}>
              <CheckoutForm clientSecret={clientSecret} checkoutToken={checkoutToken} />
            </Elements>
          )}
        </div>
      </PageTemplate>
    </CheckoutStyles>
  );
};

export default Checkout;
